import { useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import { SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMs, formatMsTick } from "../../format";
import { NEUTRAL, SERIES, SERIES_LIGHT, VERDICT_COLORS } from "../../palette";
import type { PipelineLatency, PipelineSection } from "../../types";
import { VERDICT_ORDER } from "./helpers";

/** the four latencies of the flow, in their fixed colour order */
const LATENCIES: PipelineLatency[] = ["trigger_rpc_ms", "upload_delay_ms", "pipeline_ms", "capture_to_verdict_ms"];

/** Captured pizzas per bucket by verdict, orders, and the usual volume of the same time on past days. */
export function FlowChartCard({ data }: { data: PipelineSection }) {
  const { t } = useLocalization();
  const series = useMemo<ChartSeries[]>(() => {
    const out: ChartSeries[] = VERDICT_ORDER.map((verdict) => ({
      key: `verdict_${verdict}`,
      label: t(`monitoring.verdict.${verdict}`),
      color: VERDICT_COLORS[verdict],
      type: "bar",
      stackId: "verdict",
      values: data.series[`verdict.${verdict}`] ?? [],
    }));
    out.push({ key: "orders", label: t("monitoring.series.orders"), color: SERIES[0], values: data.series.orders ?? [] });
    if (data.baseline) {
      out.push(
        { key: "usual_captured", label: t("monitoring.series.usualCaptured"), color: NEUTRAL.baseline, dashed: true, values: data.baseline.captured },
        { key: "usual_orders", label: t("monitoring.series.usualOrders"), color: SERIES_LIGHT, dashed: true, values: data.baseline.orders },
      );
    }
    return out;
  }, [data, t]);

  return (
    <SectionCard
      title={t("monitoring.overview.flow")}
      description={t("monitoring.overview.flowDescription", { window: t(`monitoring.window.long.${data.window}`) })}
    >
      <TimeSeriesChart
        t={data.t}
        stepMs={data.step_ms}
        window={data.window}
        series={series}
        height={240}
        format={formatCount}
        integer
        emptyText={t("monitoring.noData")}
      />
      <p className="mt-2 text-[11px] text-muted-foreground">
        {data.baseline
          ? t("monitoring.pipeline.flow.baseline", { days: data.baseline.days })
          : t("monitoring.pipeline.flow.noBaseline")}
      </p>
    </SectionCard>
  );
}

/** p95 of every latency of the flow per bucket (p50 behind the legend) and the window totals. */
export function LatencyCard({ data }: { data: PipelineSection }) {
  const { t } = useLocalization();
  const series = useMemo<ChartSeries[]>(
    () => [
      ...LATENCIES.map((name, i) => ({
        key: `${name}_p95`,
        label: `${t(`monitoring.pipeline.latency.${name}`)} ${t("monitoring.series.p95")}`,
        color: SERIES[i],
        values: data.series[`${name}.p95`] ?? [],
      })),
      ...LATENCIES.map((name, i) => ({
        key: `${name}_p50`,
        label: `${t(`monitoring.pipeline.latency.${name}`)} ${t("monitoring.series.p50")}`,
        color: SERIES[i],
        dashed: true,
        hidden: true,
        values: data.series[`${name}.p50`] ?? [],
      })),
    ],
    [data, t],
  );

  return (
    <SectionCard
      title={t("monitoring.pipeline.latency.title")}
      description={t("monitoring.pipeline.latency.description", { window: t(`monitoring.window.long.${data.window}`) })}
    >
      <div className="flex flex-col gap-3">
        <TimeSeriesChart
          t={data.t}
          stepMs={data.step_ms}
          window={data.window}
          series={series}
          height={240}
          format={formatMs}
          tickFormat={formatMsTick}
          emptyText={t("monitoring.noData")}
        />
        <Table className="text-xs">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-7 px-1.5">{t("monitoring.pipeline.latency.stage")}</TableHead>
              <TableHead className="hidden h-7 px-1.5 text-right sm:table-cell">{t("monitoring.pipeline.col.n")}</TableHead>
              <TableHead className="h-7 px-1.5 text-right">{t("monitoring.pipeline.col.avg")}</TableHead>
              <TableHead className="h-7 px-1.5 text-right">{t("monitoring.series.p50")}</TableHead>
              <TableHead className="h-7 px-1.5 text-right">{t("monitoring.series.p95")}</TableHead>
              <TableHead className="hidden h-7 px-1.5 text-right sm:table-cell">{t("monitoring.series.p99")}</TableHead>
              <TableHead className="h-7 px-1.5 text-right">{t("monitoring.pipeline.col.max")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LATENCIES.map((name, i) => {
              const hist = data.totals.latency[name];
              return (
                <TableRow key={name} className="hover:bg-transparent">
                  <TableCell className="px-1.5 py-1.5" title={t(`monitoring.pipeline.latency.${name}Hint`)}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-3 shrink-0" style={{ borderTop: `2px solid ${SERIES[i]}` }} aria-hidden />
                      <span className="break-words">{t(`monitoring.pipeline.latency.${name}`)}</span>
                    </span>
                  </TableCell>
                  <TableCell className="hidden px-1.5 py-1.5 text-right font-mono tabular-nums text-muted-foreground sm:table-cell">{formatCount(hist?.n ?? 0)}</TableCell>
                  <TableCell className="px-1.5 py-1.5 text-right font-mono tabular-nums">{formatMs(hist?.avg)}</TableCell>
                  <TableCell className="px-1.5 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{formatMs(hist?.p50)}</TableCell>
                  <TableCell className="px-1.5 py-1.5 text-right font-mono tabular-nums">{formatMs(hist?.p95)}</TableCell>
                  <TableCell className="hidden px-1.5 py-1.5 text-right font-mono tabular-nums sm:table-cell">{formatMs(hist?.p99)}</TableCell>
                  <TableCell className="px-1.5 py-1.5 text-right font-mono tabular-nums">{formatMs(hist?.max)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </SectionCard>
  );
}
